import React from 'react';
import { Helmet } from 'react-helmet-async';
import Button from './Button';
import { BRAND, brandify } from '../brandConfig';

const industries = [
  { name: 'Restaurants & Cafés', blurb: 'Menus, online ordering links, and reservation-ready restaurant website design that fills tables.' },
  { name: 'Contractors & Home Services', blurb: 'Lead-generating sites for plumbers, electricians, remodelers, and landscapers — built to rank and convert calls.' },
  { name: 'Law Firms & Professional Services', blurb: 'Credible, fast attorney and consultant websites that turn searches into consultations.' },
  { name: 'Real Estate', blurb: 'Agent and brokerage website design with listing showcases and lead capture.' },
  { name: 'Health & Wellness', blurb: 'Clean, trustworthy sites for clinics, gyms, salons, and studios with booking integration.' },
  { name: 'Retail & E-commerce', blurb: 'Product-forward small business storefronts engineered for conversion.' },
];

const WebDesignChicagoPage = ({ navigate }) => {
  return (
    <div className="py-32 max-w-[95vw] mx-auto px-4 md:px-8 bg-background min-h-screen flex flex-col justify-between">
      <Helmet>
        <title>{`Chicago Website Design for Small Business | ${BRAND}`}</title>
        <meta name="description" content={brandify('Chicago website designer for small businesses. Custom web design for restaurants, contractors, law firms, real estate, and more — from $299 setup + $59/mo. Serving Chicago and all of Chicagoland.')} />
        <meta name="keywords" content={`Chicago website design, website designer near me, Chicago web design for small business, restaurant website design Chicago, contractor website design Chicago, ${BRAND}`} />
        <link rel="canonical" href="https://www.wind-marketing.com/web-design-chicago" />
        <meta property="og:title" content={`Chicago Website Design for Small Business • ${BRAND}`} />
        <meta property="og:description" content={brandify('Chicago website designer for small businesses. Custom, affordable web design from $299 setup + $59/mo.')} />
        <meta property="og:url" content="https://www.wind-marketing.com/web-design-chicago" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Chicago Small Business Website Design",
            "provider": {
              "@type": "ProfessionalService",
              "@id": "https://www.wind-marketing.com/#organization",
              "name": "${BRAND}",
              "url": "https://www.wind-marketing.com"
            },
            "description": "${brandify('Custom website design for Chicago small businesses — restaurants, contractors, law firms, real estate, health and wellness, and retail. Affordable packages with SEO built in.')}",
            "url": "https://www.wind-marketing.com/web-design-chicago",
            "serviceType": "Website Design",
            "areaServed": [
              { "@type": "City", "name": "Chicago" },
              { "@type": "AdministrativeArea", "name": "Cook County" },
              { "@type": "State", "name": "Illinois" }
            ]
          }
        `}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto mt-12 mb-20 flex-grow w-full">
        {/* Header */}
        <div className="w-full mb-20">
          <h1
            className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter leading-none mb-8 text-foreground"
            style={{ fontFamily: "'Poppins Black', sans-serif" }}
          >
            CHICAGO WEBSITE DESIGN
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tighter mb-6 text-accent">
            A Website Designer Near You — Built for Small Business
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-medium">
            Searching for a website designer near me in Chicago? {BRAND} is a Chicago-based web design studio building custom, conversion-focused websites for small businesses across Chicagoland — from the Loop to the suburbs. No offshore templates, no agency bloat: direct collaboration, fast turnaround, and packages priced for small business budgets starting at $299 setup + $59/month.
          </p>
        </div>

        {/* Industries */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tighter mb-10 text-foreground">
            Website Design for Chicago Industries
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map(({ name, blurb }) => (
              <div key={name} className="border-2 border-border p-6">
                <h3 className="text-xl md:text-2xl font-bold uppercase tracking-tighter mb-3 text-foreground">{name}</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">{blurb}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why local */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tighter mb-6 text-foreground">
            Why Chicago Businesses Choose {BRAND}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-medium mb-6">
            Every site we build ships with responsive design, on-page SEO, fast load speeds, and contact form integration. Our Pro plan adds full SEO and AI search optimization so your business shows up when Chicago customers search — on Google and in AI assistants. See our{' '}
            <a href="/web-design" onClick={(e) => { e.preventDefault(); navigate('/web-design'); }} className="text-accent font-bold hover:underline">small business web design services</a>{' '}
            or{' '}
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="text-accent font-bold hover:underline">website design packages and pricing</a>.
          </p>
        </section>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-12">
          <Button
            size="large"
            onClick={() => navigate('/contact', { serviceInterest: 'chicago web design' })}
          >
            Get a Free Quote
          </Button>
          <Button variant="outline" size="large" onClick={() => navigate('/work')}>
            View Our Work
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WebDesignChicagoPage;
